import { useEffect, useMemo, useState } from "react";
import { Dimensions, Platform, ScrollView, Text, View } from "react-native";
import { EMBER, semanticColors } from "@louise/moon-ui/theme/tokens";
import {
  Dialog,
  DialogBodyText,
  DialogHeader,
  MoonStepper,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  projectFertilityPrediction,
  type ObservedValue,
  type ProjectionInput,
  type ProjectionStageId,
} from "@src/packages/fertility-projections";
import { OutcomeDotFunnelRows } from "./OutcomeDotFunnelRows";

type Props = {
  visible: boolean;
  cycleType: string;
  frozenEggs?: number;
  frozenEmbryos?: number;
  patientAge?: number | null;
  onClose: () => void;
};

type FunnelStep = {
  label: string;
  value: number;
  detail: string;
};

type FunnelState = {
  values: number[];
  edited: boolean[];
};

type FunnelStepKey = {
  id: ProjectionStageId;
  labelKey: string;
  detailKey: string;
  editable: boolean;
};

const EGG_FUNNEL_STEP_KEYS: FunnelStepKey[] = [
  {
    id: "frozen_oocytes",
    labelKey: "cryo_vault.potential.frozen_eggs",
    detailKey: "cryo_vault.potential.stored_cohort_detail",
    editable: true,
  },
  {
    id: "thawed_oocytes",
    labelKey: "cryo_vault.potential.survive_thaw",
    detailKey: "cryo_vault.potential.survive_thaw_detail",
    editable: true,
  },
  {
    id: "fertilized_oocytes",
    labelKey: "cryo_vault.potential.fertilize",
    detailKey: "cryo_vault.potential.fertilize_detail",
    editable: true,
  },
  {
    id: "blastocysts",
    labelKey: "cryo_vault.potential.reach_blastocyst",
    detailKey: "cryo_vault.potential.reach_blastocyst_detail",
    editable: true,
  },
  {
    id: "euploid_embryos",
    labelKey: "homepage.outcome_funnel.euploids_label",
    detailKey: "homepage.outcome_funnel.euploids_detail",
    editable: true,
  },
  {
    id: "live_births",
    labelKey: "cryo_vault.potential.estimated_live_births",
    detailKey: "cryo_vault.potential.live_birth_detail",
    editable: false,
  },
];

const EMBRYO_FUNNEL_STEP_KEYS: FunnelStepKey[] = [
  {
    id: "frozen_embryos",
    labelKey: "cryo_vault.potential.frozen_embryos",
    detailKey: "cryo_vault.potential.stored_cohort_detail",
    editable: true,
  },
  {
    id: "warmed_embryos",
    labelKey: "cryo_vault.potential.survive_thaw",
    detailKey: "cryo_vault.potential.survive_thaw_detail",
    editable: true,
  },
  {
    id: "transferred_embryos",
    labelKey: "cryo_vault.potential.combined_transfer_ready",
    detailKey: "cryo_vault.potential.combined_transfer_ready_detail",
    editable: true,
  },
  {
    id: "implanted_embryos",
    labelKey: "cryo_vault.potential.combined_implant",
    detailKey: "cryo_vault.potential.combined_implant_detail",
    editable: false,
  },
  {
    id: "live_births",
    labelKey: "cryo_vault.potential.estimated_live_births",
    detailKey: "cryo_vault.potential.live_birth_detail",
    editable: false,
  },
];

function observedValue(value: number | undefined, stageId: string) {
  if (value == null) return undefined;
  return {
    value,
    provenance: {
      sourceType: "patient_reported" as const,
      sourceId: `family-outlook-${stageId}`,
    },
  } satisfies ObservedValue<number>;
}

function buildProjectionInput(
  state: FunnelState,
  kind: "eggs" | "embryos",
  patientAge?: number | null,
): ProjectionInput {
  const valueFor = (index: number) =>
    state.edited[index] ? state.values[index] : undefined;
  const patient = patientAge
    ? {
        ageAtRetrieval: observedValue(patientAge, "patient-age"),
      }
    : {};

  if (kind === "embryos") {
    return {
      kind: "embryo_transfer",
      patient,
      embryos: {
        frozen: observedValue(valueFor(0), "frozen-embryos"),
        warmed: observedValue(valueFor(1), "warmed-embryos"),
        transferred: observedValue(valueFor(2), "transferred-embryos"),
      },
      goal: { kind: "expected_stage_count", stage: "live_births" },
    };
  }

  const euploid = valueFor(4);
  return {
    kind: "frozen_oocyte_cohort",
    patient,
    oocytes: {
      frozen: observedValue(valueFor(0), "frozen-oocytes"),
      thawed: observedValue(valueFor(1), "thawed-oocytes"),
      fertilized: observedValue(valueFor(2), "fertilized-oocytes"),
      blastocysts: observedValue(valueFor(3), "blastocysts"),
    },
    ...(euploid == null
      ? {}
      : {
          embryos: {
            euploid: observedValue(euploid, "euploid-embryos"),
          },
        }),
    goal: { kind: "expected_stage_count", stage: "live_births" },
  };
}

export function OutcomeFunnelModal({
  visible,
  cycleType,
  frozenEggs = 0,
  frozenEmbryos = 0,
  patientAge,
  onClose,
}: Props) {
  const t = useTranslations();
  const inventoryKind =
    cycleType.toLowerCase().includes("embryo") ||
    (frozenEggs === 0 && frozenEmbryos > 0)
      ? "embryos"
      : "eggs";
  const stepKeys =
    inventoryKind === "embryos"
      ? EMBRYO_FUNNEL_STEP_KEYS
      : EGG_FUNNEL_STEP_KEYS;
  const startingInventory =
    inventoryKind === "embryos" ? frozenEmbryos : frozenEggs;
  const initialState = useMemo<FunnelState>(
    () => ({
      values: [startingInventory, ...stepKeys.slice(1).map(() => 0)],
      edited: [startingInventory > 0, ...stepKeys.slice(1).map(() => false)],
    }),
    [startingInventory, stepKeys],
  );
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (visible) setState(initialState);
  }, [initialState, visible]);

  const projectionInput = useMemo(
    () => buildProjectionInput(state, inventoryKind, patientAge),
    [inventoryKind, patientAge, state],
  );
  const projection = useMemo(
    () => projectFertilityPrediction(projectionInput),
    [projectionInput],
  );

  const funnel: FunnelStep[] = stepKeys.map((step, index) => ({
    label: t(step.labelKey),
    detail: t(step.detailKey),
    value: Math.max(
      0,
      Math.round(
        projection.stages.find((stage) => stage.id === step.id)?.estimate
          .mean ?? 0,
      ),
    ),
  }));

  const handleAdjust = (index: number, value: number) => {
    setState((current) => {
      const values = [...current.values];
      const edited = [...current.edited];
      values[index] = Math.max(0, Math.round(value));
      edited[index] = true;
      return {
        values,
        edited,
      };
    });
  };

  const funnelStages = funnel.map((step, index) => ({
    id: `${stepKeys[index]?.id ?? index}-outlook`,
    label: step.label,
    value: step.value,
    detail: step.detail,
    trailing: stepKeys[index]?.editable ? (
      <MoonStepper
        value={step.value}
        onChange={(next) => handleAdjust(index, next)}
        accentColor={semanticColors.accent.default}
      />
    ) : undefined,
  }));

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={onClose}
      layout={Platform.OS === "web" ? "centered" : "bottom"}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: Dimensions.get("window").height * 0.84 }}
    >
      <View style={{ maxHeight: Dimensions.get("window").height * 0.78 }}>
        <DialogHeader
          title={t("homepage.outcome_funnel.family_outlook")}
          subtitle={t("homepage.outcome_funnel.cycle_estimate", {
            params: { cycleType },
          })}
          style={{ marginBottom: 16 }}
        />

        <ScrollView
          style={{ maxHeight: Dimensions.get("window").height * 0.64 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 14, paddingBottom: 8 }}
        >
          <DialogBodyText style={{ marginTop: 0 }}>
            {t("homepage.outcome_funnel.intro")}
          </DialogBodyText>
          <OutcomeDotFunnelRows
            stages={funnelStages}
            totalDots={Math.max(
              1,
              ...funnel.map((step) => Math.max(step.value, 0)),
            )}
            embedded
          />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              lineHeight: 16,
              color: EMBER.colors.textFaint,
            }}
          >
            {t("homepage.outcome_funnel.disclaimer")}
          </Text>
        </ScrollView>
      </View>
    </Dialog>
  );
}
